import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Image, Pressable, StyleSheet, Text, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';

import { StarRating } from '@/components/ui/StarRating';
import { fontSize, radius, spacing } from '@/constants/theme';
import type { AppColors } from '@/constants/theme';
import { useTheme } from '@/contexts/ThemeContext';
import type { Review, ReviewTag } from '@/lib/database.types';
import { reviewMaskName } from '@/lib/format';
import { tagLabel, tagSentiment } from '@/lib/reviews';

type Props = {
  review: Review;
  reviewerName: string;
  reviewerNickname?: string | null;
  reviewerReliability?: number | null;
  agreementCode?: string;
  disputedLoss?: boolean;
  onPhotoPress?: (uri: string) => void;
};

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('el-GR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
  } catch {
    return iso;
  }
}

function ratingColor(rating: number, colors: AppColors): string {
  if (rating >= 4) return colors.success;
  if (rating === 3) return colors.warning;
  return colors.danger;
}

export function ReviewCard({ review, reviewerName, reviewerNickname, reviewerReliability, agreementCode, disputedLoss, onPhotoPress }: Props) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const styles = useMemo(() => makeStyles(colors), [colors]);
  const col = ratingColor(review.rating, colors);
  return (
    <View style={styles.card}>
      {/* Αριστερά: βαθμολογία + αστέρια — Δεξιά: όνομα + ημερομηνία */}
      <View style={styles.header}>
        <View style={styles.ratingBlock}>
          <Text style={[styles.ratingNum, { color: col }]}>{review.rating.toFixed(1)}</Text>
          <StarRating value={review.rating} size={13} />
        </View>
        <View style={styles.meta}>
          <View style={styles.authorRow}>
            <Ionicons name="person-outline" size={12} color={colors.textMuted} />
            <Text style={styles.author} numberOfLines={1}>{reviewMaskName(reviewerName, reviewerNickname)}</Text>
            {reviewerReliability != null && (
              <Text style={styles.reliability}>{t('reviewSummary.reviewerReliability', { percent: Math.round(reviewerReliability) })}</Text>
            )}
          </View>
          <View style={styles.dateRow}>
            <Ionicons name="calendar-outline" size={12} color={colors.textMuted} />
            <Text style={styles.date}>{formatDate(review.created_at)}</Text>
            {!!agreementCode && (
              <Text style={styles.code} numberOfLines={1} ellipsizeMode="tail"> · {agreementCode}</Text>
            )}
          </View>
        </View>
      </View>

      {disputedLoss && (
        <View style={styles.disputedBadge}>
          <Ionicons name="alert-circle-outline" size={12} color={colors.warning} />
          <Text style={styles.disputedBadgeText}>{t('reviewSummary.disputedLossBadge')}</Text>
        </View>
      )}

      {review.tags.length > 0 && (
        <View style={styles.tags}>
          {review.tags.map((t) => {
            const tag = t as ReviewTag;
            const negative = tagSentiment(tag) === 'negative';
            return (
              <View key={tag} style={[styles.tag, negative ? styles.tagNeg : styles.tagPos]}>
                <Text style={[styles.tagText, negative ? styles.tagTextNeg : styles.tagTextPos]}>
                  {tagLabel(tag)}
                </Text>
              </View>
            );
          })}
        </View>
      )}

      {!!review.photo_url && (
        <Pressable onPress={() => onPhotoPress?.(review.photo_url!)} accessibilityRole="button">
          <Image source={{ uri: review.photo_url }} style={styles.photo} />
        </Pressable>
      )}
    </View>
  );
}

function makeStyles(colors: AppColors) {
  return StyleSheet.create({
    card: {
      backgroundColor: colors.surfaceAlt,
      borderRadius: radius.md,
      borderWidth: 1,
      borderColor: colors.border,
      padding: spacing.sm,
      gap: spacing.xs,
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: spacing.sm,
    },
    ratingBlock: {
      alignItems: 'center',
      gap: 2,
      minWidth: 38,
    },
    ratingNum: {
      fontSize: fontSize.lg,
      fontWeight: '800',
      lineHeight: 22,
    },
    meta: {
      flex: 1,
      gap: 2,
    },
    authorRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
    },
    author: {
      color: colors.text,
      fontSize: fontSize.sm,
      fontWeight: '600',
      flexShrink: 1,
    },
    reliability: {
      color: colors.textMuted,
      fontSize: 10.5,
      flexShrink: 0,
    },
    dateRow: {
      flexDirection: 'row',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: 3,
    },
    date: {
      color: colors.textMuted,
      fontSize: 11,
      flexShrink: 0,
    },
    code: {
      color: colors.textMuted,
      fontSize: 11,
      fontFamily: 'monospace' as any,
      flexShrink: 1,
    },
    disputedBadge: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 4,
      alignSelf: 'flex-start',
      backgroundColor: 'rgba(251,191,36,0.10)',
      borderWidth: 1,
      borderColor: colors.warning,
      borderRadius: radius.full,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
    },
    disputedBadgeText: {
      color: colors.warning,
      fontSize: 10.5,
      fontWeight: '600',
    },
    tags: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      gap: 5,
      paddingTop: 2,
    },
    tag: {
      borderRadius: radius.full,
      paddingVertical: 3,
      paddingHorizontal: spacing.sm,
      borderWidth: 1,
    },
    tagPos: {
      backgroundColor: 'rgba(52,211,153,0.08)',
      borderColor: colors.success,
    },
    tagNeg: {
      backgroundColor: 'rgba(248,113,113,0.08)',
      borderColor: colors.danger,
    },
    tagText: {
      fontSize: 11,
      fontWeight: '600',
    },
    tagTextPos: { color: colors.success },
    tagTextNeg: { color: colors.danger },
    photo: {
      width: 96,
      height: 96,
      borderRadius: radius.md,
      backgroundColor: colors.surface,
      marginTop: 2,
    },
  });
}
